import { useId, type ReactNode } from "react";
import { Alert16Regular, Dismiss16Regular } from "@fluentui/react-icons";
import { statusStyle, type StatusDomain, type Variant } from "./status";

const cx = (...c: (string | false | undefined | null)[]) => c.filter(Boolean).join(" ");

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts.at(-1)?.[0] ?? "") : "")).toUpperCase() || "?";
}

export function Avatar({
  name,
  size = 32,
  present = false,
}: {
  name: string;
  size?: 24 | 32 | 40;
  present?: boolean;
}) {
  return (
    <span
      className={cx("gp-avatar", size === 24 && "gp-avatar--24", size === 40 && "gp-avatar--40", "t-body-sm")}
      role="img"
      aria-label={name}
    >
      <span aria-hidden>{initials(name)}</span>
      {present && <span className="gp-presence" aria-hidden />}
    </span>
  );
}

/** A person cell is always two lines: name, then email or sub-line (04 DataTable). */
export function PersonCell({
  name,
  sub,
  avatar = true,
}: {
  name: string;
  sub?: string | null | undefined;
  avatar?: boolean;
}) {
  return (
    <div className="gp-person">
      {avatar && <Avatar name={name} size={32} />}
      <div className="gp-person-text">
        <span className="t-body-strong" title={name}>
          {name}
        </span>
        {sub ? (
          <span className="gp-person-sub t-body-sm" title={sub}>
            {sub}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** Status label: never interactive, never colour alone (text always present, icon for warn and danger). */
export function Pill({
  variant = "neutral",
  icon,
  children,
}: {
  variant?: Variant;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <span className={cx("gp-pill", `gp-pill--${variant}`)}>
      {icon}
      {children}
    </span>
  );
}

/** Pill for a domain value through the single status mapping (§6). */
export function StatusPill({ domain, value, label }: { domain: StatusDomain; value: string; label: string }) {
  const s = statusStyle(domain, value);
  return (
    <Pill variant={s.variant} icon={s.icon}>
      {label}
    </Pill>
  );
}

/** Filter control; `aria-pressed` marks the active filter. */
export function Chip({
  variant = "neutral",
  pressed,
  onClick,
  children,
}: {
  variant?: "warn" | "danger" | "neutral";
  pressed?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className={cx("gp-chip", variant !== "neutral" && `gp-chip--${variant}`)}
      aria-pressed={pressed ?? false}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function StatCard({
  icon,
  label,
  value,
  caption,
  attention = false,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  caption?: ReactNode;
  attention?: boolean;
}) {
  return (
    <div className={cx("gp-statcard", attention && "gp-statcard--attention")}>
      <span className="gp-statcard-icon" aria-hidden>
        {icon}
      </span>
      <span className="gp-statcard-value">{value}</span>
      <span className="t-body gp-muted">{label}</span>
      {caption ? <span className="t-body-sm gp-faint">{caption}</span> : null}
    </div>
  );
}

export function Panel({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <div className="gp-panel" role="group" aria-label={label}>
      {children}
    </div>
  );
}

export function Card({
  title,
  children,
  tone,
  current,
  actions,
}: {
  title?: ReactNode;
  children: ReactNode;
  tone?: "danger";
  current?: boolean;
  actions?: ReactNode;
}) {
  const id = useId();
  return (
    <section
      className={cx("gp-card", tone === "danger" && "gp-card--danger", current && "gp-card--current")}
      aria-labelledby={title ? id : undefined}
    >
      {title || actions ? (
        <div className="gp-section-head">
          {title ? (
            <h2 id={id} className="t-title-sm">
              {title}
            </h2>
          ) : (
            <span />
          )}
          {actions}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function Banner({
  tone = "info",
  children,
  action,
  onDismiss,
  dismissLabel,
}: {
  tone?: "info" | "warn" | "danger" | "success" | "neutral";
  children: ReactNode;
  action?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
}) {
  const cls = tone === "info" ? "gp-banner" : `gp-banner gp-banner--${tone}`;
  return (
    <div className={cls} role={tone === "danger" || tone === "warn" ? "alert" : "status"}>
      <div className="gp-banner-text t-body">{children}</div>
      {action}
      {onDismiss && tone === "info" ? (
        <button
          type="button"
          className="gp-btn gp-btn--ghost"
          onClick={onDismiss}
          aria-label={dismissLabel ?? "Dismiss"}
        >
          <Dismiss16Regular aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

export function Callout({
  tone = "success",
  title,
  children,
  action,
}: {
  tone?: "success" | "warn";
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={cx("gp-callout", tone === "warn" && "gp-callout--warn")}>
      <span className="t-body-strong">{title}</span>
      {children ? <span className="t-body gp-muted">{children}</span> : null}
      {action}
    </div>
  );
}

export function EmptyState({ icon, title, action }: { icon?: ReactNode; title: string; action?: ReactNode }) {
  return (
    <div className="gp-empty">
      {icon ? <span aria-hidden>{icon}</span> : null}
      <p className="t-body-lg" style={{ margin: 0 }}>
        {title}
      </p>
      {action}
    </div>
  );
}

export function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="gp-stack" aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }, (_v, i) => (
        <div key={i} className="gp-skeleton" />
      ))}
    </div>
  );
}

export function Spinner({ label }: { label: string }) {
  return (
    <span role="status" className="gp-row">
      <span className="gp-spinner" aria-hidden />
      <span className="t-body-sm gp-muted">{label}</span>
    </span>
  );
}

export function ProgressBar({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = max <= 0 ? 0 : Math.min(100, Math.round((value / max) * 100));
  return (
    <div
      className="gp-progress"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-label={label}
    >
      <div style={{ width: `${pct}%` }} />
    </div>
  );
}

export interface TimelineEvent {
  id: string;
  summary: ReactNode;
  meta?: ReactNode;
  at: string;
  atLabel: string;
}

export function Timeline({ events }: { events: TimelineEvent[] }) {
  return (
    <ol className="gp-timeline">
      {events.map((e) => (
        <li key={e.id}>
          <span className="gp-timeline-dot" aria-hidden />
          <div>
            <div className="t-body-strong">{e.summary}</div>
            {e.meta ? <div className="gp-timeline-meta t-body-sm">{e.meta}</div> : null}
            <time className="gp-timeline-time t-caption" dateTime={e.at} title={e.at}>
              {e.atLabel}
            </time>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function HealthDot({ health, label }: { health: "green" | "amber" | "red"; label: string }) {
  const color =
    health === "green"
      ? "var(--success-text)"
      : health === "amber"
        ? "var(--warning-text)"
        : "var(--danger-text)";
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      style={{
        display: "inline-block",
        width: "var(--space-8)",
        height: "var(--space-8)",
        borderRadius: "var(--radius-pill)",
        background: color,
      }}
    />
  );
}

export function BarRow({
  label,
  value,
  max,
  right,
}: {
  label: string;
  value: number;
  max: number;
  right?: ReactNode;
}) {
  return (
    <div className="gp-bar-row">
      <span
        className="t-body"
        title={label}
        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {label}
      </span>
      <div className="gp-bar" aria-hidden>
        <div style={{ width: `${max <= 0 ? 0 : Math.round((value / max) * 100)}%` }} />
      </div>
      <span className="t-body-strong">{value}</span>
      {right}
    </div>
  );
}

/** Environment tag of the console (BR-260): always visible; production is the warning variant so it is never mistaken for staging. */
export function EnvTag({
  environment,
  label,
}: {
  environment: "production" | "staging" | "dev";
  label: string;
}) {
  return (
    <Pill
      variant={environment === "production" ? "warn" : "neutral"}
      icon={environment === "production" ? <Alert16Regular aria-hidden /> : undefined}
    >
      {label}
    </Pill>
  );
}

/** The only branding difference of the console: a screenshot is never mistaken for the customer product. */
export function AdminTag({ children }: { children: ReactNode }) {
  return <span className="gp-admintag t-caption-strong">{children}</span>;
}

export interface TrendPoint {
  label: string;
  value: number;
}

/** Single-series line + area chart (brand hue only, no legend); the data is also available as a visually hidden table. */
export function TrendChart({
  points,
  label,
  format,
}: {
  points: TrendPoint[];
  label: string;
  format: (v: number) => string;
}) {
  const w = 480;
  const h = 160;
  const pad = 8;
  const max = Math.max(1, ...points.map((p) => p.value));
  const x = (i: number) => pad + (points.length <= 1 ? 0 : (i * (w - 2 * pad)) / (points.length - 1));
  const y = (v: number) => h - pad - (v / max) * (h - 2 * pad);
  const line = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`)
    .join(" ");
  const area = points.length
    ? `${line} L${x(points.length - 1).toFixed(1)},${h - pad} L${x(0).toFixed(1)},${h - pad} Z`
    : "";
  return (
    <figure className="gp-trend">
      <svg viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label} preserveAspectRatio="none">
        <path d={area} className="gp-trend-area" />
        <path d={line} className="gp-trend-line" fill="none" />
      </svg>
      <table className="gp-sr-only">
        <caption>{label}</caption>
        <tbody>
          {points.map((p) => (
            <tr key={p.label}>
              <th scope="row">{p.label}</th>
              <td>{format(p.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <figcaption className="gp-trend-axis t-caption">
        <span>{points[0]?.label}</span>
        <span>{points.at(-1)?.label}</span>
      </figcaption>
    </figure>
  );
}
